import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { emergencyApi } from '../services/api';
import { format } from 'date-fns';

interface EmergencyRequestStatus {
  id: string;
  status: 'PENDING' | 'GRANTED' | 'DENIED' | 'EXPIRED' | 'REVOKED';
  reason?: string;
  expiresAt: string;
  grantedAt?: string;
  contact: {
    name: string;
    babyName: string;
  };
}

interface EmergencyPacket {
  babyName: string;
  allergies?: string[];
  medications?: string[];
  bloodType?: string;
  pediatrician?: {
    name: string;
    phone?: string;
    address?: string;
  };
  emergencyNotes?: string;
}

export default function EmergencyAccess() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [decryptedPacket, setDecryptedPacket] = useState<EmergencyPacket | null>(null);
  const [requestReason, setRequestReason] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch request status if we have a requestId and token
  const { data: requestStatus, isLoading: loadingStatus, error: statusError } = useQuery({
    queryKey: ['emergency-request', requestId, token],
    queryFn: async () => {
      if (!requestId || !token) return null;
      const response = await emergencyApi.getRequestStatus(requestId, token);
      return response.data as EmergencyRequestStatus;
    },
    enabled: !!requestId && !!token,
    refetchInterval: (query) => {
      // Poll every 5 seconds if status is PENDING
      const data = query.state.data as EmergencyRequestStatus | undefined;
      if (data?.status === 'PENDING') {
        return 5000;
      }
      return false;
    },
  });

  // Access emergency packet mutation
  const accessPacketMutation = useMutation({
    mutationFn: async () => {
      if (!requestId || !token) throw new Error('Missing request ID or token');
      const response = await emergencyApi.accessEmergencyPacket(requestId, token);
      return response.data;
    },
    onSuccess: (responseData) => {
      try {
        // The packet is JSON encrypted - parse it
        if (responseData?.encryptedAccessPacket) {
          const packet = JSON.parse(atob(responseData.encryptedAccessPacket)) as EmergencyPacket;
          setDecryptedPacket(packet);
        } else {
          setError('No emergency packet data received');
        }
      } catch (err) {
        console.error('Failed to decrypt packet:', err);
        setError('Failed to decrypt emergency information');
      }
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to access emergency information');
    },
  });

  // Request access mutation (for initial request)
  const requestAccessMutation = useMutation({
    mutationFn: async ({ contactId, reason }: { contactId: string; reason?: string }) => {
      const response = await emergencyApi.requestAccess(contactId, reason);
      return response.data;
    },
    onSuccess: () => {
      setRequestSuccess(true);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to submit emergency access request');
    },
  });

  // Auto-fetch packet when status is GRANTED
  useEffect(() => {
    if (requestStatus?.status === 'GRANTED' && !decryptedPacket && !accessPacketMutation.isPending) {
      accessPacketMutation.mutate();
    }
  }, [requestStatus?.status, decryptedPacket, accessPacketMutation.isPending]);

  // Handle initial access request
  const handleRequestAccess = async (contactId: string) => {
    setIsRequesting(true);
    setError(null);

    try {
      await requestAccessMutation.mutateAsync({ contactId, reason: requestReason || undefined });
    } finally {
      setIsRequesting(false);
    }
  };

  // Loading state
  if (loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading emergency access request...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (statusError || (!requestId && !token)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">!</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Invalid Request
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This emergency access link is invalid or has expired.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block btn-primary"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // Request success state (waiting for approval)
  if (requestSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">!</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Request Submitted
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Your emergency access request has been submitted. The primary caregiver has been notified and will review your request.
          </p>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            You will receive an email when your request is approved or denied.
          </p>
        </div>
      </div>
    );
  }

  // Status-based rendering
  if (requestStatus) {
    switch (requestStatus.status) {
      case 'PENDING':
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="text-center mb-8">
                <span className="text-4xl">!</span>
                <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                  Emergency Access Pending
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  Waiting for caregiver approval
                </p>
              </div>

              <div className="card p-6 max-w-md mx-auto">
                <div className="text-center">
                  <div className="animate-pulse mb-4">
                    <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mx-auto flex items-center justify-center">
                      <span className="text-2xl">!</span>
                    </div>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Awaiting Approval
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Your request to access emergency information for {requestStatus.contact.babyName} is pending.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    The primary caregiver has been notified. This page will automatically update when a decision is made.
                  </p>
                  <div className="mt-6 text-xs text-gray-400 dark:text-gray-500">
                    Request expires: {format(new Date(requestStatus.expiresAt), 'PPp')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'GRANTED':
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="text-center mb-8">
                <span className="text-4xl">+</span>
                <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                  Emergency Access Granted
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  Medical information for {requestStatus.contact.babyName}
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg max-w-md mx-auto">
                  {error}
                </div>
              )}

              {accessPacketMutation.isPending ? (
                <div className="card p-6 max-w-md mx-auto text-center">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Decrypting emergency information...
                  </p>
                </div>
              ) : decryptedPacket ? (
                <div className="card p-6 max-w-2xl mx-auto">
                  <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                          {decryptedPacket.babyName}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Emergency Medical Information
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full text-sm">
                        Access Granted
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Blood Type */}
                    {decryptedPacket.bloodType && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Blood Type
                        </h3>
                        <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                          {decryptedPacket.bloodType}
                        </p>
                      </div>
                    )}

                    {/* Allergies */}
                    <div className={decryptedPacket.allergies && decryptedPacket.allergies.length > 0 ? 'md:col-span-2' : ''}>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Allergies
                      </h3>
                      {decryptedPacket.allergies && decryptedPacket.allergies.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-2">
                          {decryptedPacket.allergies.map((allergy, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-full font-medium"
                            >
                              {allergy}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1 text-gray-900 dark:text-white">
                          No known allergies
                        </p>
                      )}
                    </div>

                    {/* Medications */}
                    <div className={decryptedPacket.medications && decryptedPacket.medications.length > 0 ? 'md:col-span-2' : ''}>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Current Medications
                      </h3>
                      {decryptedPacket.medications && decryptedPacket.medications.length > 0 ? (
                        <ul className="mt-1 space-y-1">
                          {decryptedPacket.medications.map((med, index) => (
                            <li
                              key={index}
                              className="text-gray-900 dark:text-white font-medium"
                            >
                              {med}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-1 text-gray-900 dark:text-white">
                          No current medications
                        </p>
                      )}
                    </div>

                    {/* Pediatrician */}
                    {decryptedPacket.pediatrician && (
                      <div className="md:col-span-2">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Pediatrician
                        </h3>
                        <div className="mt-1 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {decryptedPacket.pediatrician.name}
                          </p>
                          {decryptedPacket.pediatrician.phone && (
                            <a
                              href={`tel:${decryptedPacket.pediatrician.phone}`}
                              className="text-primary-600 dark:text-primary-400 hover:underline block mt-1"
                            >
                              {decryptedPacket.pediatrician.phone}
                            </a>
                          )}
                          {decryptedPacket.pediatrician.address && (
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                              {decryptedPacket.pediatrician.address}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Emergency Notes */}
                    {decryptedPacket.emergencyNotes && (
                      <div className="md:col-span-2">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Emergency Notes
                        </h3>
                        <p className="mt-1 text-gray-900 dark:text-white whitespace-pre-wrap">
                          {decryptedPacket.emergencyNotes}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
                    <p>
                      Access granted at: {requestStatus.grantedAt && format(new Date(requestStatus.grantedAt), 'PPp')}
                    </p>
                    <p>
                      Access expires: {format(new Date(requestStatus.expiresAt), 'PPp')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="card p-6 max-w-md mx-auto text-center">
                  <button
                    onClick={() => accessPacketMutation.mutate()}
                    className="btn-primary"
                  >
                    View Emergency Information
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'DENIED':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="max-w-md w-full text-center">
              <span className="text-6xl">X</span>
              <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                Access Denied
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Your emergency access request was denied by the primary caregiver.
              </p>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                If you believe this was a mistake, please contact the caregiver directly.
              </p>
            </div>
          </div>
        );

      case 'EXPIRED':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="max-w-md w-full text-center">
              <span className="text-6xl">!</span>
              <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                Request Expired
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                This emergency access request has expired.
              </p>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Please submit a new request if you still need access.
              </p>
            </div>
          </div>
        );

      case 'REVOKED':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="max-w-md w-full text-center">
              <span className="text-6xl">X</span>
              <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                Access Revoked
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Your emergency access has been revoked by the primary caregiver.
              </p>
            </div>
          </div>
        );
    }
  }

  // Default state - show request form (when accessed from emergency contact link without active request)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <span className="text-4xl">!</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Emergency Access Request
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Request access to emergency medical information
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg max-w-md mx-auto">
            {error}
          </div>
        )}

        <div className="card p-6 max-w-md mx-auto">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You have been designated as an emergency contact. By submitting this request, the primary caregiver will be notified and can grant you temporary access to emergency medical information.
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason for Request (optional)
              </label>
              <textarea
                id="reason"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Briefly describe the emergency situation..."
                className="input min-h-[100px]"
              />
            </div>

            <button
              onClick={() => requestId && handleRequestAccess(requestId)}
              disabled={isRequesting}
              className="btn-danger w-full"
            >
              {isRequesting ? (
                <>
                  <span className="spinner mr-2" />
                  Submitting...
                </>
              ) : (
                'Request Emergency Access'
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
            Emergency access requests are logged and audited. Only use this feature in genuine emergencies.
          </p>
        </div>
      </div>
    </div>
  );
}
